import React, { useContext, useState } from 'react';
import { NonOptional } from '../../..';
import { MedicareQuoteQuery } from '@coverright/data-access/medicare';
import { useSaveDoctors } from './use-save-doctors';
import { ProviderOutput } from '@coverright/data-access/types/medicare';
import { logEvent } from '@coverright/shared/analytics';
import { DoctorSelectionModal } from '../ui/doctor-selection-modal';

interface Props {
  handleAddClick: () => void,
  handleEditClick: (doctor: ProviderOutput) => void,
  handleDeleteClick: (doctor: ProviderOutput) => void,
  doctors: ProviderOutput[],
}

const DoctorSelectionContext = React.createContext<Props>({
  handleAddClick: () => {},
  handleEditClick: () => {},
  handleDeleteClick: () => {},
  doctors: []
})

export function withDoctorSelectionModal<P>(WrappedComponent: React.FC<P & {quote: NonOptional<MedicareQuoteQuery['medicareQuote']>}>, showSearchRadius = false) {
  return (props: P & {quote: NonOptional<MedicareQuoteQuery['medicareQuote']>}) => {
    const {preferredDoctors, zip, county} = props.quote;
    const [modalOpen, setModalOpen] = useState(false);
    const [editNpi, setEditNpi] = useState<string>();
    const saveDoctors = useSaveDoctors(props.quote);

    const handleModalClose = async (doctor?: ProviderOutput) => {
      if (doctor) {
        const doctors = [...preferredDoctors]
        const doctorIndex = doctors.findIndex(d => d.npi === doctor.npi);

        if (doctorIndex >= 0) {
          doctors[doctorIndex] = doctor;
          saveDoctors(doctors);
        } else {
          saveDoctors([...doctors, doctor]);
        }

        logEvent('Add doctor', { doctors });
      }
      setModalOpen(false);
    }

    const handleEditClick = (doctor: ProviderOutput) => {
      setEditNpi(doctor.npi);
      setModalOpen(true)
    }

    const handleDeleteClick = (doctor: ProviderOutput) => {
      saveDoctors(preferredDoctors.filter(d => d.npi !== doctor.npi));
    }

    const handleAddClick = () => {
      setEditNpi(undefined);
      setModalOpen(true)
    }

    return <DoctorSelectionContext.Provider value={{
      doctors: preferredDoctors,
      handleDeleteClick,
      handleAddClick,
      handleEditClick
    }}>
      <DoctorSelectionModal open={modalOpen}
                            zip={zip}
                            county={county!}
                            preferredDoctors={preferredDoctors}
                            showSearchRadius={showSearchRadius}
                            doctor={preferredDoctors.find(d => d.npi === editNpi)}
                            onClose={handleModalClose} />

      <WrappedComponent {...props} />
    </DoctorSelectionContext.Provider>
  }
}

export const useDoctorSelection = () => useContext(DoctorSelectionContext);
